import { useTranslation } from 'react-i18next';
import { Shield, Award, Clock, type LucideIcon } from 'lucide-react';
import { SectionWrapper, SectionHeader } from '@/components/common';

interface Reason {
  title: string;
  description: string;
  icon?: LucideIcon;
}

interface WhyChooseSectionProps {
  title?: string;
  subtitle?: string;
  reasons?: Reason[];
  serviceType?: string;
  color?: string;
  lightBg?: string;
  className?: string;
}

export function WhyChooseSection({
  title,
  subtitle,
  reasons,
  serviceType,
  color = 'text-blue-600',
  lightBg = 'bg-blue-50',
  className,
}: WhyChooseSectionProps) {
  const { t } = useTranslation('services');

  const defaultIcons = [Shield, Award, Clock];

  // If reasons not provided, build from translations
  const displayReasons = reasons || (serviceType ? [1, 2, 3].map((num, index) => ({
    title: t(`${serviceType}.whyChoose.reason${num}.title`),
    description: t(`${serviceType}.whyChoose.reason${num}.description`),
    icon: defaultIcons[index],
  })) : []);

  const displayTitle = title || (serviceType ? t(`${serviceType}.whyChoose.title`) : '');
  const displaySubtitle = subtitle || (serviceType ? t(`${serviceType}.whyChoose.subtitle`) : '');

  return (
    <SectionWrapper bgColor="bg-gray-50" className={className}>
      <SectionHeader
        title={displayTitle}
        subtitle={displaySubtitle}
        subtitleClassName="text-lg text-gray-600 max-w-2xl mx-auto"
        className="mb-12"
      />

      <div className="grid md:grid-cols-3 gap-8">
        {displayReasons.map((reason, index) => {
          const Icon = reason.icon || defaultIcons[index % defaultIcons.length];
          return (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow">
              <div className={`w-14 h-14 ${lightBg} rounded-2xl flex items-center justify-center mb-6`}>
                <Icon className={`w-7 h-7 ${color}`} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {reason.title}
              </h3>
              <p className="text-gray-600">
                {reason.description}
              </p>
            </div>
          );
        })}
      </div>
    </SectionWrapper>
  );
}
